import React from 'react';
import { ExternalLink, Printer, QrCode, RefreshCw, Save, Settings2 } from 'lucide-react';

import { DEPT_FULL_NAMES as DEPT_NAMES } from '@/lib/constants';
import { STATUS_COLORS } from '@/lib/status-colors';
import { ActionRow, StatusPill, TopBar } from '@/components/phone/primitives';
import { Colors, priorityTone, statusTone } from '@/components/phone/theme';

/**
 * The top of a job card, on a phone.
 *
 * The desktop header is one row: back arrow, title, up to six badges and four
 * buttons. At a handset's width the badges wrapped under the title and the
 * buttons wrapped under those, so the thing the page is about was buried under
 * two rows of chrome. Here the bar names the job, the pills say what state it
 * is in, and the actions are one row that scrolls.
 */
export default function PhoneJobCardHeader({
  jobCard, info = {}, splitEligibility, splitTypes = [],
  canPrint, canFill, requiredMissing = 0, hasChanges, saving,
  onBack, onPrint, onQr, onSpecs, onSave, onOpenParent,
}) {
  const status = STATUS_COLORS[jobCard.status] || STATUS_COLORS.draft;
  const title = jobCard.is_standalone
    ? (jobCard.standalone_name || 'Standalone Job Card')
    : (jobCard.item_name || jobCard.product_subtype || 'Job Card');
  const subtitle = jobCard.is_standalone
    ? `ID ${jobCard.id?.slice(0, 8)}`
    : `Order ${jobCard.order_display_id}${info.quantity ? ` · Qty ${info.quantity.toLocaleString()}` : ''}`;

  return (
    <div className="-mx-3 -mt-3 md:-mx-4 md:-mt-4" data-testid="job-card-header-phone">
      <TopBar
        eyebrow={`JOB CARD · ${DEPT_NAMES[jobCard.product_type_code] || jobCard.product_type_name || ''}`}
        title={title}
        subtitle={subtitle}
        onBack={onBack}
      />
      <div
        className="border-b px-4 pt-2"
        style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
      >
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill label={status.label} tone={statusTone(jobCard.status)} dot />
          {!!jobCard.type_name && <StatusPill label={jobCard.type_name} tone="accent" />}
          {info.priority === 'high' && <StatusPill label="High priority" tone={priorityTone('high')} />}
          {jobCard.is_standalone && <StatusPill label="Standalone" tone="caution" />}
          {splitEligibility?.already_split && (
            <StatusPill label={`Split into ${splitTypes.length} types`} tone="caution" />
          )}
          {splitEligibility?.is_child && (
            <button
              type="button"
              onClick={onOpenParent}
              className="flex items-center gap-1 text-[11px] font-semibold"
              style={{ color: Colors.accent.DEFAULT }}
            >
              <ExternalLink className="h-3 w-3" /> View parent
            </button>
          )}
        </div>

        <ActionRow
          actions={[
            canPrint && { key: 'print', label: 'Print', icon: Printer, onClick: onPrint, testID: 'print-job-card-btn' },
            { key: 'qr', label: 'QR sticker', icon: QrCode, onClick: onQr, testID: 'download-qr-btn' },
            canFill && {
              key: 'specs', label: 'Specifications', icon: Settings2, onClick: onSpecs,
              tone: 'accent', testID: 'open-specs-btn', badge: requiredMissing > 0 ? requiredMissing : null,
            },
            {
              key: 'save', label: saving ? 'Saving…' : hasChanges ? 'Save changes' : 'All saved',
              icon: saving ? RefreshCw : Save, onClick: onSave, primary: hasChanges,
              disabled: !hasChanges || saving, testID: 'save-btn',
            },
          ]}
        />
      </div>
    </div>
  );
}
